import React, { useState, useEffect } from 'react'
import api from '../services/api'
import { LineChart, BarChart, DonutChart, GaugeChart } from '../components/charts'

interface OverviewData {
  sellers: { total: number; active: number }
  orders: { total: number; total_revenue: number; avg_order_value: number }
  products: { total: number; categories: number }
}

interface GrowthData {
  current_period: { orders: number; revenue: number; active_sellers: number }
  previous_period: { orders: number; revenue: number; active_sellers: number }
  growth: { orders_growth: number; revenue_growth: number; sellers_growth: number }
}

interface TrendDataPoint {
  period: string
  order_count: number
  revenue: number
  active_sellers: number
}

interface MarketData {
  country: string
  seller_count: number
  order_count: number
  total_revenue: number
  avg_order_value: number
}

interface CategoryData {
  category: string
  product_count: number
  active_count: number
}

interface ProductData {
  product_id: string
  name: string
  category: string
  sku: string
  units_sold: number
  revenue: number
  avg_price: number
}

const SellerAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('30d')
  const [overview, setOverview] = useState<OverviewData | null>(null)
  const [growth, setGrowth] = useState<GrowthData | null>(null)
  const [trends, setTrends] = useState<TrendDataPoint[]>([])
  const [markets, setMarkets] = useState<MarketData[]>([])
  const [categories, setCategories] = useState<CategoryData[]>([])
  const [topProducts, setTopProducts] = useState<ProductData[]>([])

  useEffect(() => {
    fetchDashboardData()
  }, [timeRange])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/seller-analytics/dashboard?time_range=${timeRange}`)
      const data = response.data

      setOverview(data.overview)
      setGrowth(data.growth)
      setTrends(data.trends?.trends || [])
      setMarkets(data.market_performance?.markets || [])
      setCategories(data.categories?.categories || [])
      setTopProducts(data.top_products?.products || [])
    } catch (error) {
      console.error('Failed to fetch seller analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value)

  const formatNumber = (value: number) =>
    new Intl.NumberFormat('en-GB').format(value)

  const formatGrowth = (value: number) => {
    const sign = value >= 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}%`
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>Loading...</div>
          <div style={{ color: '#6B7280' }}>Fetching seller analytics data</div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1F2937', margin: 0 }}>
            Seller Performance Analytics
          </h1>
          <p style={{ color: '#6B7280', marginTop: '4px' }}>
            Comprehensive overview of seller metrics and market performance
          </p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            fontSize: '14px',
            cursor: 'pointer',
            backgroundColor: 'white'
          }}
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>Total Revenue</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1F2937' }}>
            {overview ? formatCurrency(overview.orders.total_revenue) : '-'}
          </div>
          {growth && (
            <div style={{
              fontSize: '13px',
              color: growth.growth.revenue_growth >= 0 ? '#10B981' : '#EF4444',
              marginTop: '8px'
            }}>
              {formatGrowth(growth.growth.revenue_growth)} vs previous period
            </div>
          )}
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>Total Orders</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1F2937' }}>
            {overview ? formatNumber(overview.orders.total) : '-'}
          </div>
          {growth && (
            <div style={{
              fontSize: '13px',
              color: growth.growth.orders_growth >= 0 ? '#10B981' : '#EF4444',
              marginTop: '8px'
            }}>
              {formatGrowth(growth.growth.orders_growth)} vs previous period
            </div>
          )}
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>Active Sellers</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1F2937' }}>
            {overview ? formatNumber(overview.sellers.active) : '-'}
          </div>
          {growth && (
            <div style={{
              fontSize: '13px',
              color: growth.growth.sellers_growth >= 0 ? '#10B981' : '#EF4444',
              marginTop: '8px'
            }}>
              {formatGrowth(growth.growth.sellers_growth)} vs previous period
            </div>
          )}
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>Avg Order Value</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1F2937' }}>
            {overview ? formatCurrency(overview.orders.avg_order_value) : '-'}
          </div>
          <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>
            {overview ? formatNumber(overview.products.total) : '-'} products
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>
        {/* Revenue Trends */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Revenue Trends</h3>
          {trends.length > 0 ? (
            <LineChart
              data={trends.map(t => ({ date: t.period, value: t.revenue }))}
              width={650}
              height={300}
              yLabel="Revenue (GBP)"
              colors={['#4A90E2']}
              showGrid={true}
              animate={true}
            />
          ) : (
            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>
              No trend data available
            </div>
          )}
        </div>

        {/* Growth Gauges */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Growth Metrics</h3>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            {growth && (
              <>
                <GaugeChart
                  value={Math.min(100, Math.max(0, 50 + growth.growth.revenue_growth))}
                  min={0}
                  max={100}
                  width={200}
                  height={120}
                  label="Revenue Growth"
                  unit="%"
                  thresholds={[
                    { value: 40, color: '#EF4444' },
                    { value: 60, color: '#F59E0B' },
                    { value: 100, color: '#10B981' }
                  ]}
                />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937' }}>
                    {formatGrowth(growth.growth.revenue_growth)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>Revenue Growth</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        {/* Market Performance */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Revenue by Market</h3>
          {markets.length > 0 ? (
            <BarChart
              data={markets.map(m => ({ label: m.country, value: m.total_revenue }))}
              width={450}
              height={300}
              yLabel="Revenue (GBP)"
              colors={['#4A90E2', '#50C878', '#FF6B35', '#7B68EE', '#E74C3C']}
              animate={true}
            />
          ) : (
            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>
              No market data available
            </div>
          )}
        </div>

        {/* Category Distribution */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Product Categories</h3>
          {categories.length > 0 ? (
            <DonutChart
              data={categories.map(c => ({ label: c.category, value: c.product_count }))}
              width={400}
              height={300}
              showLabels={true}
              showLegend={true}
              centerText="Products"
              colors={['#4A90E2', '#50C878', '#FF6B35', '#7B68EE', '#E74C3C', '#F59E0B', '#10B981']}
              animate={true}
            />
          ) : (
            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>
              No category data available
            </div>
          )}
        </div>
      </div>

      {/* Top Products Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Top Performing Products</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Product</th>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Category</th>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>SKU</th>
              <th style={{ textAlign: 'right', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Units Sold</th>
              <th style={{ textAlign: 'right', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Revenue</th>
              <th style={{ textAlign: 'right', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Avg Price</th>
            </tr>
          </thead>
          <tbody>
            {topProducts.length > 0 ? (
              topProducts.map((product) => (
                <tr key={product.product_id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    <div style={{ fontWeight: '500' }}>{product.name}</div>
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {product.category}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px', color: '#6B7280' }}>{product.sku}</td>
                  <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>{formatNumber(product.units_sold)}</td>
                  <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(product.revenue)}</td>
                  <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>{formatCurrency(product.avg_price)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>
                  No products data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Market Performance Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginTop: '24px'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Market Performance Details</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Market</th>
              <th style={{ textAlign: 'right', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Sellers</th>
              <th style={{ textAlign: 'right', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Orders</th>
              <th style={{ textAlign: 'right', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Revenue</th>
              <th style={{ textAlign: 'right', padding: '12px', fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>Avg Order Value</th>
            </tr>
          </thead>
          <tbody>
            {markets.length > 0 ? (
              markets.map((market) => (
                <tr key={market.country} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{market.country}</td>
                  <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>{formatNumber(market.seller_count)}</td>
                  <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>{formatNumber(market.order_count)}</td>
                  <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(market.total_revenue)}</td>
                  <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>{formatCurrency(market.avg_order_value)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>
                  No market data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default SellerAnalytics
